import { useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { TagsInput } from 'react-tag-input-component';

import { TargetType } from '@/types/Types';
import { ROUTE_LIST_ACTION_URL } from '@/utils/constants';
import InputError from '@/components/common/Messages/InputError';
import { NewSwitch } from '@/components/common/NewSwitch';
import {
  PageDynamicType,
  ProcedureTypes,
  RouterType,
  useDynamicPagesQuery,
  VersionStates,
} from '@/components/common/Providers/GraphqlProvider/generated';

import InputFormContext from '../Inputs/InputFormContext';
import InputSelectContext from '../Inputs/InputSelectContext';
import InputSelectShadcn from '../Inputs/InputSelectShadcn';

type Props = {
  isSimpleUrl?: boolean;
  title?: string;
  subtitle?: string;
  fieldUrl?: string;
  fieldTarget?: string;
  fieldRouteType?: string;
  fieldArguments?: string;
  fieldRouteNavigation?: string;
  isDisabledAll?: boolean;
  showSwitch?: boolean;
  includeWorkflowOption?: boolean;
  topContent?: React.ReactNode;
  children?: React.ReactNode;
  // dynamicSection?: DynamicPageSection;
};

const FIELD_TARGET = 'target';
const FIELD_ACTION = 'routeType';
const FIELD_URL = 'url';
const ARGUMENTS_URL = 'arguments';
const FIELD_URL_NAVIGATION = 'routeNavigation';

const ActionURL = ({
  isSimpleUrl,
  title = 'Enlaces',
  subtitle = 'Gestione el comportamiento de los enlaces en la sede electrónica',
  fieldUrl = FIELD_URL,
  fieldTarget = FIELD_TARGET,
  fieldRouteType = FIELD_ACTION,
  fieldArguments = ARGUMENTS_URL,
  fieldRouteNavigation = FIELD_URL_NAVIGATION,
  isDisabledAll = false,
  showSwitch = false,
  includeWorkflowOption = false,
  topContent,
  children,
  // dynamicSection
}: Props) => {
  const [switchEnabled, setSwitchEnabled] = useState(true);
  const { data } = useDynamicPagesQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      where: {
        // status: VersionStates.Active,
        status: [VersionStates.Active],
      },
    },
  });

  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();

  useEffect(() => {
    initValues();
  }, []);

  const initValues = () => {
    if (!watch(fieldRouteType)) {
      setValue(fieldRouteType, RouterType.NoAction);
    }

    if (
      !watch(fieldTarget) ||
      (watch(fieldTarget) != TargetType._blank && watch(fieldTarget) != TargetType._self)
    ) {
      setValue(fieldTarget, '_self');
    }
    return '';
  };

  const typeOfAction = watch(fieldRouteType);

  const dynamicPages = useMemo(() => {
    if (!data?.pageDynamics) {
      return [];
    }
    // if (dynamicSection) {
    //   return data?.pageDynamics?.map((x) => {
    //     return {
    //       label: x.title,
    //       value: x.id,
    //     };
    //   });
    // }

    let dataToUse = data?.pageDynamics;

    if (typeOfAction === RouterType.Microsite) {
      dataToUse = dataToUse.filter((x) => x.type === PageDynamicType.Microsite);
    }

    if (typeOfAction === RouterType.DynamicPage) {
      dataToUse = dataToUse.filter((x) => x.type === PageDynamicType.DynamicPage);
    }

    return dataToUse?.map((x) => {
      return {
        label: x.title,
        value: x?.seo?.metadata?.at(0)?.slug || x.id,
      };
    });
    // }, [data, dynamicSection]);
  }, [data, typeOfAction]);

  // useEffect(() => {
  //   if (watch(fieldRouteType) == RouterType.DynamicPage) {
  //     setValue(fieldRouteNavigation, null)
  //     // setValue(fieldRouteNavigation, SubPages.Home)
  //   }
  // }, [watch(fieldRouteType)])

  const ACTION_OPTIONS = [
    {
      label: 'Sin acción',
      value: RouterType.NoAction,
    },
    {
      label: 'Abrir enlace externo',
      value: RouterType.ExternalRoute,
    },
    {
      label: 'Abrir enlace interno',
      value: RouterType.InternaltRoute,
    },
    {
      label: 'Abrir enlace interno con argumentos',
      value: RouterType.InternalRouteWithArguments,
    },
    {
      label: 'Abrir página dinámica',
      value: RouterType.DynamicPage,
    },
    {
      label: 'Abrir micro sitio',
      value: RouterType.Microsite,
    },
    ...(includeWorkflowOption
      ? [
          {
            label: 'Iniciar trámite (Workflow)',
            value: 'WORKFLOW' as unknown as RouterType,
          },
        ]
      : []),
  ];

  const ACTION_VIDEO_OPTIONS = [
    {
      label: 'Abrir enlace externo',
      value: RouterType.ExternalRoute,
    },
  ];

  const TARGET_OPTIONS = [
    {
      label: 'Abrir en misma pestaña',
      value: '_self',
    },
    {
      label: 'Abrir en nueva pestaña',
      value: '_blank',
    },
  ];

  const finalDisabled = isDisabledAll || (showSwitch && !switchEnabled);

  return (
    <div className="flex flex-1 flex-col">
      <div className="flex items-center justify-between">
        <h3 className="headline-h5 text-primary">{title}</h3>
        {showSwitch && (
          // <div className="flex items-center gap-2">
          //   <label className="body-text1 text-tundora">
          //     {switchEnabled ? 'Activado' : 'Desactivado'}
          //   </label>
          //   <input
          //     type="checkbox"
          //     checked={switchEnabled}
          //     onChange={() => setSwitchEnabled((v) => !v)}
          //     className="form-switch"
          //     aria-label="Habilitar sección"
          //   />
          // </div>
          <NewSwitch
            className="flex-row-reverse gap-2.5"
            onCheckedChange={() => setSwitchEnabled((v) => !v)}
            checked={switchEnabled}
            label={'Sección con enlace'}
          />
        )}
      </div>
      <hr className="my-[5px]" />
      <label className="body-text1 text-tundora">{subtitle}</label>
      {topContent && <div className="mt-4">{topContent}</div>}
      <br />

      <div className="mb-2 flex flex-1 flex-row gap-x-10">
        <div className="flex flex-1 flex-col">
          {includeWorkflowOption ||
          !isSimpleUrl ||
          watch('typeofprocedure') === ProcedureTypes.InformationOfInterest ? (
            <InputSelectShadcn
              options={ACTION_OPTIONS}
              currentId={fieldRouteType}
              title={'Tipo de acción'}
              disabled={finalDisabled}
            />
          ) : (
            <InputSelectShadcn
              options={ACTION_VIDEO_OPTIONS}
              currentId={fieldRouteType}
              title={'Tipo de acción'}
              disabled={finalDisabled}
            />
          )}
        </div>

        {watch(fieldRouteType) !== RouterType.NoAction && (
          <div className="flex flex-1 flex-col">
            <InputSelectShadcn
              options={TARGET_OPTIONS}
              currentId={fieldTarget}
              title={'Tipo de comportamiento'}
              disabled={finalDisabled}
            />
          </div>
        )}
      </div>
      {children && <div className="mt-4">{children}</div>}
      <br />

      {(watch(fieldRouteType) == RouterType.ExternalRoute ||
        watch(fieldRouteType) == RouterType.InternalRouteWithArguments) &&
      watch(fieldRouteType) !== RouterType.NoAction ? (
        <>
          <InputFormContext title={'Dirección URL'} currentId={fieldUrl} disabled={finalDisabled} />
          {!isSimpleUrl || watch('typeofprocedure') === ProcedureTypes.InformationOfInterest ? (
            <div className="mt-2">
              <label className="body-text1 text-tundora">Argumentos</label>
              <div className="mt-[8px]">
                <Controller
                  name={fieldArguments}
                  defaultValue={[]}
                  render={({ field: { onChange, value } }) => (
                    <TagsInput
                      separators={['Tab', 'Enter']}
                      onChange={onChange}
                      value={value ?? []}
                      disabled={finalDisabled}
                    />
                  )}
                />
                <InputError message={errors[fieldArguments]?.message as string} />
              </div>
            </div>
          ) : null}
        </>
      ) : watch(fieldRouteType) !== RouterType.NoAction ? (
        initValues()
      ) : null}
      {watch(fieldRouteType) == RouterType.InternaltRoute &&
        watch(fieldRouteType) !== RouterType.NoAction && (
          <InputSelectShadcn
            options={ROUTE_LIST_ACTION_URL}
            currentId={fieldRouteNavigation}
            title={'Páginas internas'}
            disabled={finalDisabled}
            searchable
            searchPlaceholder="Buscar página..."
          />
        )}

      {(watch(fieldRouteType) == RouterType.DynamicPage ||
        watch(fieldRouteType) == RouterType.Microsite) &&
        watch(fieldRouteType) !== RouterType.NoAction && (
          <InputSelectShadcn
            options={dynamicPages}
            currentId={fieldUrl}
            title={
              watch(fieldRouteType) == RouterType.Microsite ? 'Micro sitios' : 'Páginas dinámicas'
            }
            disabled={finalDisabled}
            searchable
            searchPlaceholder={
              watch(fieldRouteType) == RouterType.Microsite
                ? 'Buscar micro sitio...'
                : 'Buscar página dinámica...'
            }
          />
        )}
    </div>
  );
};

export default ActionURL;
